import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Select } from "@/components/ds";
import Header from "@/components/catalogue/Header";
import Hero from "@/components/catalogue/Hero";
import MatchWidget from "@/components/catalogue/MatchWidget";
import FilterRail from "@/components/catalogue/FilterRail";
import ProductCard from "@/components/catalogue/ProductCard";
import EnquiryModal from "@/components/catalogue/EnquiryModal";
import useIsMobile from "@/hooks/useIsMobile";

const EMPTY_FILTERS = { segments: [], categories: [], fabrics: [], minPrice: "", maxPrice: "" };
const SORT_OPTIONS = ["Price: low to high", "Price: high to low", "MOQ: low to high", "Lead time"];
const PAGE_SIZE = 12;

export default function Catalogue({ region = "all" }) {
  const isMobile = useIsMobile();
  const showMoq = !new URLSearchParams(window.location.search).has("hideMoq");

  const [stats, setStats] = useState(null);
  const [facets, setFacets] = useState(null);
  const [skuMap, setSkuMap] = useState({});
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [currency, setCurrency] = useState("INR");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [matchData, setMatchData] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, product: null, intent: "bulk" });

  const buildParams = useCallback((f, pageArg) => {
    const p = { page: pageArg, page_size: PAGE_SIZE };
    if (f.segments.length) p.segment = f.segments.join(",");
    if (f.categories.length) p.category = f.categories.join(",");
    if (f.fabrics.length) p.fabric = f.fabrics.join(",");
    if (f.minPrice) p.min_price = f.minPrice;
    if (f.maxPrice) p.max_price = f.maxPrice;
    if (sort) p.sort = sort;
    else if (matchData) p.order = matchData.ranked.map((r) => r.code).join(",");
    p.region = region;
    return p;
  }, [sort, matchData, region]);

  const fetchGrid = useCallback(async (pageArg, replace) => {
    try {
      const { data } = await api.get("/skus", { params: buildParams(filters, pageArg) });
      setTotal(data.total);
      setCurrency(data.currency || "INR");
      setItems((prev) => (replace ? data.items : [...prev, ...data.items]));
    } catch (e) {
      toast.error(apiErr(e));
    }
  }, [filters, buildParams]);

  // initial loads
  useEffect(() => {
    api.get("/stats", { params: { region } }).then((r) => setStats(r.data)).catch(() => {});
    api.get("/skus/facets", { params: { region } }).then((r) => setFacets(r.data)).catch(() => {});
    api.get("/skus", { params: { page: 1, page_size: 500, region } }).then((r) => {
      const m = {};
      r.data.items.forEach((s) => { m[s.code] = s; });
      setSkuMap(m);
    }).catch(() => {});
  }, [region]);

  // refetch grid when applied filters / sort / match / region change
  useEffect(() => {
    setPage(1);
    fetchGrid(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sort, matchData, region]);

  const scores = useMemo(() => {
    const m = {};
    if (matchData) matchData.ranked.forEach((r) => { m[r.code] = r.score; });
    return m;
  }, [matchData]);

  const topMatches = useMemo(() => {
    if (!matchData) return [];
    return matchData.ranked.slice(0, 3).map((r) => {
      const s = skuMap[r.code] || {};
      return {
        name: s.name || r.code,
        scoreLabel: `${r.score}%`,
        pct: r.score,
        image: s.images?.[0],
        specs: s.fabric ? `${s.fabric} · ${s.weight} · ${s.priceLabel}/pc · MOQ ${s.moqLabel}` : "",
        why: r.why,
      };
    });
  }, [matchData, skuMap]);

  const onFind = async (file, text) => {
    if (!file && !text.trim()) { toast.error("Add a photo/tech pack or describe the style"); return; }
    setMatchLoading(true);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      if (text) fd.append("text", text);
      const { data } = await api.post("/match", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSort("");
      setMatchData(data);
      toast.success("Matched to catalogue");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setMatchLoading(false);
    }
  };

  const onClearMatch = () => setMatchData(null);

  const removeFrom = (field, val) => setFilters((f) => ({ ...f, [field]: f[field].filter((x) => x !== val) }));
  const clearScalar = (fields) => setFilters((f) => ({ ...f, ...fields }));

  const chips = [];
  filters.segments.forEach((s) => chips.push({ label: s, remove: () => removeFrom("segments", s) }));
  filters.categories.forEach((c) => chips.push({ label: c, remove: () => removeFrom("categories", c) }));
  filters.fabrics.forEach((f) => chips.push({ label: f, remove: () => removeFrom("fabrics", f) }));
  if (filters.minPrice || filters.maxPrice) chips.push({ label: `${currency === "USD" ? "$" : "₹"}${filters.minPrice || 0}–${filters.maxPrice || "∞"}`, remove: () => clearScalar({ minPrice: "", maxPrice: "" }) });

  const openModal = (product, intent) => setModal({ open: true, product, intent });

  return (
    <div style={{ minHeight: "100vh", background: "var(--surface-muted)" }}>
      <Header onTalk={() => openModal(null, "bulk")} />
      <Hero stats={stats} />

      <div style={{ maxWidth: 1320, margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0,240px) minmax(0,1fr)", gap: isMobile ? 14 : 32, padding: isMobile ? "14px 14px 44px" : 32, alignItems: "start" }}>
        <FilterRail facets={facets} filters={filters} setFilters={setFilters} onClear={() => setFilters(EMPTY_FILTERS)} isMobile={isMobile} />

        <main style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>
            <div>
              <h2 style={{ margin: 0, font: `var(--fw-semibold) ${isMobile ? 21 : 26}px/1.2 var(--font-display)`, letterSpacing: "-0.015em", color: "var(--text-primary)" }}>Available styles</h2>
              <p data-testid="grid-count" style={{ margin: "6px 0 0", font: "var(--fw-regular) 14px/1.4 var(--font-sans)", color: "var(--text-secondary)" }}>
                Showing {items.length} of {total} SKUs · in-stock pricing, ex-factory
              </p>
            </div>
            <div style={{ minWidth: isMobile ? "auto" : 220, width: isMobile ? "100%" : "auto" }}>
              <span className="lf-eyebrow" style={{ display: "block", marginBottom: 6 }}>Sort by</span>
              <Select data-testid="sort-select" size="sm" placeholder="Featured" options={SORT_OPTIONS} value={sort} onChange={(e) => setSort(e.target.value)} />
            </div>
          </div>

          {chips.length > 0 && (
            <div data-testid="active-chips" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ font: "var(--fw-regular) 13px/1 var(--font-sans)", color: "var(--text-tertiary)" }}>Active:</span>
              {chips.map((chip, i) => (
                <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 11px", borderRadius: "var(--radius-pill)", background: "var(--lf-blue-50)", color: "var(--lf-blue-700)", font: "var(--fw-medium) 12px/1 var(--font-sans)" }}>
                  {chip.label}
                  <span data-testid={`chip-remove-${i}`} role="button" tabIndex={0} onClick={chip.remove} style={{ cursor: "pointer", fontSize: 14, lineHeight: 1, color: "var(--lf-blue-600)" }}>×</span>
                </span>
              ))}
            </div>
          )}

          {items.length === 0 ? (
            <div style={{ padding: "60px 20px", textAlign: "center", background: "var(--surface-card)", border: "1px dashed var(--border-strong)", borderRadius: "var(--radius-xl)", color: "var(--text-tertiary)", font: "var(--fw-regular) 14px/1.5 var(--font-sans)" }}>
              No styles match these filters. Try clearing a few.
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,minmax(0,1fr))" : "repeat(auto-fill,minmax(210px,1fr))", gap: isMobile ? 12 : 16 }}>
              {items.map((p) => (
                <ProductCard
                  key={p.code}
                  p={{ ...p, matchScore: matchData ? scores[p.code] : null }}
                  showMoq={showMoq}
                  currency={currency}
                  onQuery={(prod) => openModal(prod, "bulk")}
                  onSample={(prod) => openModal(prod, "sample")}
                />
              ))}
            </div>
          )}

          {items.length < total && (
            <div style={{ display: "flex", justifyContent: "center", marginTop: 6 }}>
              <button data-testid="load-more-btn" onClick={() => { const n = page + 1; setPage(n); fetchGrid(n, false); }} style={{ padding: "10px 20px", borderRadius: "var(--radius-md)", border: "1px solid var(--border-strong)", background: "var(--surface-card)", color: "var(--text-primary)", cursor: "pointer", font: "var(--fw-semibold) 13px/1 var(--font-sans)" }}>Load more</button>
            </div>
          )}

        </main>
      </div>

      <EnquiryModal open={modal.open} product={modal.product} intent={modal.intent} region={region} onClose={() => setModal((m) => ({ ...m, open: false }))} />
      <MatchWidget
        matched={!!matchData}
        loading={matchLoading}
        extracted={matchData?.extracted || []}
        topMatches={topMatches}
        onFind={onFind}
        onClear={onClearMatch}
      />
    </div>
  );
}
